import { REQUIRED_LIGHTING } from "@/features/presets/params"
import type { TUTORIAL_KIND } from "@/features/tutorial/Tutorial.svelte"
import { checkDesktop } from "@/libs/hardware/desktop"
import { compose } from "@/libs/lodash"

/** Options to configure LIQA‘s look and behavior. */
export type Props = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /** A string containing valid CSS rules. */
  styles?: string
  /** An object whose keys are translation keys and values are translation texts. */
  messages?: Messages
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: PRESET
  /**
   * Overrides for the default preset‘s quality settings.
   * @hidden
   * @deprecated Use `requiredLighting`.
   */
  settings?: Partial<{ lighting: number }>
  /**
   * A list of sources to capture the image from.
   *
   * If "front_camera" is specified, the front camera will be used to capture the image.
   * If the device has no front camera but has a back camera - the back camera will be used as a fallback.
   *
   * If "back_camera" is specified, the back camera will be used to capture the image.
   * If the device has no back camera but has a front camera - the front camera will be used as a fallback.
   *
   * If more than one camera is specified as a source, the first one will be used as the initial one.
   *
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default ["front_camera"]
   */
  sources?: SOURCE[]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * The option is only applicable to "hair" preset. For other presets it is always "auto".
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will adapt to the lighting conditions.
   * - `"default"`: the minimum requirements will be set to optimal values which will provide more details on the photo.
   *
   * Default value: `"default"`
   */
  requiredLighting?: REQUIRED_LIGHTING
  /**
   * A flag to enable or disable the effects during the image capture process.
   *
   * @default false
   */
  effects?: boolean
  /**
   * A flag to enable or disable tutorial option on the source selection screen.
   * Enabled only when sources option contain "front_camera" or "back_camera" and "upload".
   *
   * - `"video"`: a video tutorial will be shown.
   * - `"image"`: an image tutorial will be shown. Can be used as a fallback for reducing the loading time.
   *
   * @default "video"
   */
  tutorial?: TUTORIAL_KIND
  /**
   * A flag to set the quality check behavior.
   *
   * - `off`: the quality check will be disabled.
   * - `strict`: the quality check will be strict and the user will not be able to continue if the quality criteria are not met.
   * - `normal`: the quality check will be normal and the user will be able to continue even if the quality criteria are not met.
   *
   * @default "off"
   */
  qualityCheck?: QUALITY_CHECK
  /**
   * An array specifying which capture sources should enforce quality checks.
   *
   * The quality check behavior (set via the `qualityCheck` property) will only be applied for the sources included in this array.
   * Available values are:
   * - `front_camera`: Enforces quality check on images captured from the front camera.
   * - `back_camera`: Enforces quality check on images captured from the back camera.
   * - `upload`: Enforces quality check on images provided via file upload.
   * - `companion`: Enforces quality check on images captured via a companion source.
   *
   * If not provided, quality checks will be applied for all sources.
   *
   * @default ["front_camera","back_camera","upload","companion"]
   */
  qualityCheckSources?: SOURCE[]
}

export type Messages = {
  [key: string]: string | Messages
}
export enum PRESET {
  FACE = "face",
  FACE_180 = "face-180",
  HAIR = "hair",
}
export enum SOURCE {
  FRONT_CAMERA = "front_camera",
  BACK_CAMERA = "back_camera",
  UPLOAD = "upload",
  COMPANION = "companion",
}

export enum QUALITY_CHECK {
  OFF = "off",
  NORMAL = "normal",
  STRICT = "strict",
}

export const DEFAULT_PRESET = PRESET.FACE

export const DEFAULT_QUALITY_CHECK_SOURCES = [
  SOURCE.FRONT_CAMERA,
  SOURCE.BACK_CAMERA,
  SOURCE.UPLOAD,
  SOURCE.COMPANION,
]

const validateSources =
  <P extends string, A extends Record<P, SOURCE[]>, D extends Record<keyof A, SOURCE[]>>({
    allowedSourcesByPreset,
    defaultSourcesByPreset,
  }: {
    allowedSourcesByPreset: A
    defaultSourcesByPreset?: D
  }) =>
  <T extends string[]>(
    sources: T,
    { preset, showWarning }: { preset: Props["preset"]; showWarning?: boolean },
  ): SOURCE[] => {
    const allowedSources = preset && allowedSourcesByPreset[preset as P]

    if (!allowedSources) return sources as SOURCE[]

    const adjustedSources = sources.filter((source) => {
      const isAllowedSource = allowedSources.includes(source as SOURCE)

      if (!isAllowedSource && showWarning)
        console.warn(
          `[LIQA] Warning: the "${source}" source is not supported for the "${preset}" preset.`,
        )

      return isAllowedSource
    }) as SOURCE[]

    if (adjustedSources.length > 0) return adjustedSources

    const defaultSources = defaultSourcesByPreset?.[preset as P] ?? allowedSources

    if (showWarning)
      console.warn(
        `[LIQA] Warning: No valid sources were provided for the selected preset ("${preset}"). ` +
          (defaultSources.length > 0
            ? `Defaulting to [${defaultSources.map((s) => `"${s}"`).join(", ")}]. `
            : "") +
          "Please check the presets and image sources documentation and make sure you are using the correct sources. https://docs.liqa.haut.ai/customization/user-flow/image-sources.html",
      )

    return defaultSources
  }
const filterSourcesByRules =
  (rules: Partial<Record<SOURCE, () => boolean>>) => (sources: SOURCE[]) =>
    sources.filter((source) => rules[source]?.() ?? true)
const provideDefaultSources = (sources: SOURCE[]) =>
  sources.length === 0 || !sources.some((s) => s !== SOURCE.COMPANION)
    ? [...sources, SOURCE.FRONT_CAMERA]
    : sources

export const adjustPresetSources = compose(
  filterSourcesByRules({ [SOURCE.COMPANION]: checkDesktop }),
  provideDefaultSources,
  validateSources({
    defaultSourcesByPreset: { [PRESET.FACE_180]: [SOURCE.FRONT_CAMERA] },
    allowedSourcesByPreset: {
      // face-180 has no upload and back_camera options
      [PRESET.FACE_180]: [SOURCE.FRONT_CAMERA, SOURCE.COMPANION],
    },
  }),
)

/** Converts old sources to modern providing backward compatibility and warns of deprecated props. */
export const oldSourcesToNewSources = <T extends string[]>(sources: T): SOURCE[] => {
  const DEPRECATED_SOURCES_MAP = {
    photo: SOURCE.UPLOAD,
    camera: SOURCE.FRONT_CAMERA,
  }

  return sources.map((source) => {
    const modernSource = DEPRECATED_SOURCES_MAP[source as keyof typeof DEPRECATED_SOURCES_MAP]

    if (!modernSource) return source as SOURCE

    console.warn(
      `[LIQA] Deprecation warning: the "${source}" source has been deprecated. Use "${modernSource}" instead.`,
    )

    return modernSource
  })
}

export function settingsToRequiredLighting(settings?: Props["settings"]) {
  if (settings)
    console.warn(
      '[LIQA] Warning: "settings" attribute is deprecated. Use "requiredLighting" instead.',
    )

  return settings?.lighting === 0 ? REQUIRED_LIGHTING.NONE : undefined
}

export const knownOrDefault = <T>({
  value,
  knownValues,
  defaultValue,
  message,
}: {
  value: T
  knownValues: T[]
  defaultValue: T
  message?: (value: T, defaultValue: T) => string
}) => {
  if (knownValues.includes(value)) return value
  if (message) console.warn(message(value, defaultValue))
  return defaultValue
}
